
export const getLocationsApi = async (id, accessToken) => {
    // Validate location ID
    if (!id) {
        throw new Error('Location ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch location from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/get-location`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ id })
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch location data';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: {...} } or { success: true, data: [...] }
    if (result.success && result.data !== undefined) {
        // Handle both object and array responses
        const locationData = Array.isArray(result.data) 
            ? (result.data.length > 0 ? result.data[0] : null)
            : (result.data || null);
        
        if (!locationData) {
            throw new Error('Location data not found');
        }
        
        return locationData;
    } else {
        throw new Error('Unexpected response format from location API');
    }
};

export const getLocationListApi = async (businessId, accessToken) => {
    // Validate business_id
    if (!businessId) {
        throw new Error('Business ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch locations from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/all-locations`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ business_id: businessId })
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch location list';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: [] }
    if (result.success && result.data) {
        // Ensure we always return an array
        return Array.isArray(result.data) ? result.data : [];
    } else {
        // Fallback if response format is unexpected
        throw new Error('Unexpected response format from location API');
    }
};

export const deleteLocationApi = async (id, accessToken) => {
    // Validate id
    if (!id) {
        throw new Error('Location ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/location/${id}`,
        {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to delete location';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from delete location API');
    }
};

export const addLocationsApi = async (formData, id, accessToken) => {
    // Validate business_id
    if (!id) {
        throw new Error('Business ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    const locationsData = formData.map((formData) => {
        const normalizedZip =
            formData.zipCode && String(formData.zipCode).trim() !== ''
                ? formData.zipCode
                : '0';

        return {
            name: formData.location_name,
            phone: formData.location_phone_no,
            address: formData.location_address,
            city: formData.city,
            state: formData.state,
            zip: normalizedZip,
            business_id: id,
            suite: formData.suite,
        };
    });

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/location`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(locationsData)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to add locations';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from location API');
    }
};

export const addLocationApi = async (formData, businessId, accessToken) => {
    // Validate business_id
    if (!businessId) {
        throw new Error('Business ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Normalize zip code
    const normalizedZip =
        formData.zipCode && String(formData.zipCode).trim() !== ''
            ? formData.zipCode
            : '0';

    // Build payload matching backend expectations
    // Backend validates location_name and location_phone_no, then maps to database columns
    const payload = {
        business_id: businessId,
        location_name: formData.location_name,
        location_phone_no: formData.location_phone_no,
        location_address: formData.location_address || '',
        city: formData.city || '',
        state: formData.state || '',
        zipCode: normalizedZip,
        suite: formData.suite || '',
    };

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/add-location`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to add location';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from add location API');
    }
};
export const updateLocationApi = async (formData, selectedLocation, accessToken) => {
    // Validate required fields
    if (!selectedLocation?.id) {
        throw new Error('Location ID is required');
    }

    if (!selectedLocation?.business_id) {
        throw new Error('Business ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Normalize zip code
    const normalizedZip =
        formData.zipCode && String(formData.zipCode).trim() !== ''
            ? formData.zipCode
            : '0';

    // Build payload matching backend expectations
    // Backend validates location_name and location_phone_no, then maps to database columns
    const payload = {
        id: selectedLocation.id,
        business_id: selectedLocation.business_id,
        location_name: formData.location_name,
        location_phone_no: formData.location_phone_no,
        location_address: formData.location_address || '',
        city: formData.city || '',
        state: formData.state || '',
        zipCode: normalizedZip,
        suite: formData.suite || '',
    };

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/update-location`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to update location';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from update location API');
    }
};

export const getBusinessDataApi = async (businessId, accessToken) => {
    // Validate business_id
    if (!businessId) {
        throw new Error('Business ID is required');
    }

    

    // Fetch business from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/business/${businessId}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch business data';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data } or { success: true, data: [] }
    if (result.success && result.data !== undefined) {
        let businessData = result.data;

        // If multiple businesses exist for this user, prefer the most recently created one
        // so that new businesses show up correctly in the UI.
        if (Array.isArray(businessData) && businessData.length > 1) {
            businessData = [...businessData].sort((a, b) => {
                const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
                const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
                return bTime - aTime; // newest first
            });
        }
        
        // Store the "active" business (the first after sorting) in localStorage
        if (Array.isArray(businessData) && businessData.length > 0) {
            localStorage.setItem('business', JSON.stringify(businessData[0]));
        } else if (!Array.isArray(businessData) && businessData) {
            localStorage.setItem('business', JSON.stringify(businessData));
        }
        
        return businessData;
    } else {
        throw new Error('Unexpected response format from business API');
    }
};


// New function to get business data for both business owners and staff members
export const getBusinessDataForUserApi = async (userId, accessToken) => {
    // Validate user_id
    if (!userId) {
        throw new Error('User ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch business from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/get-business`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ userid: userId })
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch business data';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data } or { success: true, data: [] }
    if (result.success && result.data !== undefined) {
        const raw = result.data;
        // Normalize to array (backend may return single object or array)
        const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : []);
        // When multiple businesses (e.g. from different queries), prefer the one owned by this user (userid match), then most recent (highest id)
        const sorted = list.length > 1
            ? [...list].sort((a, b) => {
                const aOwned = a.userid === userId ? 1 : 0;
                const bOwned = b.userid === userId ? 1 : 0;
                if (bOwned !== aOwned) return bOwned - aOwned;
                return (b.id ?? 0) - (a.id ?? 0);
            })
            : list;
        const businessToUse = sorted[0];

        if (businessToUse) {
            localStorage.setItem('business', JSON.stringify(businessToUse));
        }

        return sorted;
    } else {
        throw new Error('Unexpected response format from business API');
    }
};

export const getLocationForBusinessDataApi = async (businessId, accessToken) => {
    // Validate business ID
    if (!businessId) {
        throw new Error('Business ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/all-locations`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ business_id: businessId })
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch locations';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: [] } or the locations array directly
    if (result.success && result.data) {
        return result.data;
    } else if (Array.isArray(result)) {
        // If result is the locations array directly
        return result;
    } else {
        throw new Error('Unexpected response format from get locations API');
    }
};

export const getProviderApi = async (id, accessToken) => {
    // Validate provider ID
    if (!id) {
        throw new Error('Provider ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch provider from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch provider data';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: [...] } where data is an array
    if (result.success && result.data) {
        // If data is an array, return the first element (the provider object)
        // If data is already an object, return it directly
        if (Array.isArray(result.data)) {
            if (result.data.length === 0) {
                throw new Error('Provider not found');
            }
            return result.data[0];
        }
        return result.data;
    } else {
        throw new Error('Unexpected response format from provider API');
    }
}

export const getProvidersByIdsApi = async (ids, accessToken) => {
    // Validate provider IDs
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
        return [];
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch providers from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/providers`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ ids })
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch providers data';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data }
    if (result.success && result.data !== undefined) {
        // Preserve object structure - do not convert to array
        return result.data;
    } else {
        throw new Error('Unexpected response format from providers API');
    }
}

export const getProviderEducationApi = async (id, accessToken) => {
    // Validate provider ID
    if (!id) {
        throw new Error('Provider ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch provider education from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}/education`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch provider education';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data }
    if (result.success && result.data !== undefined) {
        return { data: result.data };
    } else {
        throw new Error('Unexpected response format from provider education API');
    }
}
export const getProviderCertificateApi = async (id, accessToken) => {
    // Validate provider ID
    if (!id) {
        throw new Error('Provider ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch provider certificates from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}/certificates`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch provider certificates';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data }
    if (result.success && result.data !== undefined) {
        return { data: result.data };
    } else {
        throw new Error('Unexpected response format from provider certificates API');
    }
}
export const getProviderQualificationApi = async (id, accessToken) => {
    // Validate provider ID
    if (!id) {
        throw new Error('Provider ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch provider qualification from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}/qualification`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch provider qualification';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data }
    if (result.success && result.data !== undefined) {
        return { data: result.data };
    } else {
        throw new Error('Unexpected response format from provider qualification API');
    }
}

export const addProviderQualificationApi = async (qualificationData, accessToken) => {
    // Validate qualification data
    if (!qualificationData || !Array.isArray(qualificationData) || qualificationData.length === 0) {
        throw new Error('Qualification data is required and must be a non-empty array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of qualification objects
    const payload = qualificationData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/qualifications`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to add provider qualifications';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from add provider qualification API');
    }
}

export const updateProviderQualificationApi = async (providerId, qualificationData, accessToken) => {
    // Validate provider ID
    if (!providerId) {
        throw new Error('Provider ID is required');
    }

    // Validate qualification data
    if (!qualificationData || !Array.isArray(qualificationData)) {
        throw new Error('Qualification data is required and must be an array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of qualification objects
    const payload = qualificationData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}/qualifications`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to update provider qualifications';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data !== undefined) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from update provider qualification API');
    }
}

// Certificates APIs
export const addProviderCertificateApi = async (certificateData, accessToken) => {
    // Validate certificate data
    if (!certificateData || !Array.isArray(certificateData) || certificateData.length === 0) {
        throw new Error('Certificate data is required and must be a non-empty array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of certificate objects
    const payload = certificateData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/certificates`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to add provider certificates';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from add provider certificate API');
    }
}

export const updateProviderCertificateApi = async (providerId, certificateData, accessToken) => {
    // Validate provider ID
    if (!providerId) {
        throw new Error('Provider ID is required');
    }

    // Validate certificate data
    if (!certificateData || !Array.isArray(certificateData)) {
        throw new Error('Certificate data is required and must be an array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of certificate objects
    const payload = certificateData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}/certificates`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to update provider certificates';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data !== undefined) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from update provider certificate API');
    }
}

// Education APIs
export const addProviderEducationApi = async (educationData, accessToken) => {
    // Validate education data
    if (!educationData || !Array.isArray(educationData) || educationData.length === 0) {
        throw new Error('Education data is required and must be a non-empty array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of education objects
    const payload = educationData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/education`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to add provider education';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from add provider education API');
    }
}

export const updateProviderEducationApi = async (providerId, educationData, accessToken) => {
    // Validate provider ID
    if (!providerId) {
        throw new Error('Provider ID is required');
    }

    // Validate education data
    if (!educationData || !Array.isArray(educationData)) {
        throw new Error('Education data is required and must be an array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of education objects
    const payload = educationData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}/education`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to update provider education';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data !== undefined) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from update provider education API');
    }
}

// Licenses APIs
export const addProviderLicenseApi = async (licenseData, accessToken) => {
    // Validate license data
    if (!licenseData || !Array.isArray(licenseData) || licenseData.length === 0) {
        throw new Error('License data is required and must be a non-empty array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of license objects
    const payload = licenseData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/licenses`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to add provider licenses';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from add provider license API');
    }
}

export const updateProviderLicenseApi = async (providerId, licenseData, accessToken) => {
    // Validate provider ID
    if (!providerId) {
        throw new Error('Provider ID is required');
    }

    // Validate license data
    if (!licenseData || !Array.isArray(licenseData)) {
        throw new Error('License data is required and must be an array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of license objects
    const payload = licenseData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}/licenses`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to update provider licenses';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data !== undefined) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from update provider license API');
    }
}

// Specialities APIs
export const addProviderSpecialityApi = async (specialityData, accessToken) => {
    // Validate speciality data
    if (!specialityData || !Array.isArray(specialityData) || specialityData.length === 0) {
        throw new Error('Speciality data is required and must be a non-empty array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of speciality objects
    const payload = specialityData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/specialities`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to add provider specialities';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from add provider speciality API');
    }
}

export const updateProviderSpecialityApi = async (providerId, specialityData, accessToken) => {
    // Validate provider ID
    if (!providerId) {
        throw new Error('Provider ID is required');
    }

    // Validate speciality data
    if (!specialityData || !Array.isArray(specialityData)) {
        throw new Error('Speciality data is required and must be an array');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send array of speciality objects
    const payload = specialityData;

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}/specialities`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to update provider specialities';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data, message: '...' }
    if (result.success && result.data !== undefined) {
        return result.data;
    } else {
        throw new Error('Unexpected response format from update provider speciality API');
    }
}
export const getProviderSpecialityApi = async (id, accessToken) => {
    // Validate provider ID
    if (!id) {
        throw new Error('Provider ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch provider speciality from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}/speciality`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch provider speciality';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data }
    if (result.success && result.data !== undefined) {
        return { data: result.data };
    } else {
        throw new Error('Unexpected response format from provider speciality API');
    }
}
export const getProviderLicenceApi = async (id, accessToken) => {
    // Validate provider ID
    if (!id) {
        throw new Error('Provider ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch provider licenses from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}/licenses`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch provider licenses';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data }
    if (result.success && result.data !== undefined) {
        return { data: result.data };
    } else {
        throw new Error('Unexpected response format from provider licenses API');
    }
}
export const getProviderAvailabilityApi = async (id, accessToken) => {
    // Validate provider ID
    if (!id) {
        throw new Error('Provider ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch provider availability from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}/availability`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch provider availability';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data } or the data directly
    if (result.success && result.data !== undefined) {
        return { data: result.data };
    } else if (Array.isArray(result)) {
        // If result is an array directly
        return { data: result };
    } else {
        throw new Error('Unexpected response format from provider availability API');
    }
}

export const updateProviderAvailabilityApi = async (providerId, availabilityData, accessToken, formState = 'edit') => {
    // Validate provider ID
    if (!providerId) {
        throw new Error('Missing required field: provider_id is required');
    }

    if (!availabilityData.business_id) {
        throw new Error('Missing required field: business_id is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload with formState
    const payload = {
        ...availabilityData,
        formState: formState
    };

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}/availability`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || result.message || 'Failed to update provider availability';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data }
    if (result.success && result.data !== undefined) {
        return result.data;
    } else if (Array.isArray(result)) {
        // If result is an array directly
        return result;
    } else {
        throw new Error('Unexpected response format from update provider availability API');
    }
}
export const getProviderServicesApi = async (id, accessToken) => {
    // Validate provider ID
    if (!id) {
        throw new Error('Provider ID is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Fetch provider services from backend API
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}/services`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to fetch provider services';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data } or the data directly
    if (result.success && result.data !== undefined) {
        return { data: result.data };
    } else if (Array.isArray(result)) {
        // If result is an array directly
        return { data: result };
    } else {
        throw new Error('Unexpected response format from provider services API');
    }
}



// Create provider profile API
export const createProviderProfileApi = async (providerData, accessToken) => {
    // Validate required fields
    if (!providerData.business_id) {
        throw new Error('Missing required field: business_id is required');
    }
    if (!providerData.first_name || providerData.first_name.trim() === '') {
        throw new Error('Missing required field: first_name is required');
    }
    if (!providerData.last_name || providerData.last_name.trim() === '') {
        throw new Error('Missing required field: last_name is required');
    }
    if (!providerData.email || providerData.email.trim() === '') {
        throw new Error('Missing required field: email is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - send provider profile data
    const payload = {
        business_id: providerData.business_id,
        first_name: providerData.first_name?.trim(),
        last_name: providerData.last_name?.trim(),
        email: providerData.email?.toLowerCase()?.trim(),
        bio: providerData.bio || null,
        profile_url: providerData.profile_url || null,
        sex: providerData.sex || providerData.gender || null,
        image_url: providerData.image_url || null,
        status: providerData.status || 'draft',
        handle: providerData.handle || null,
    };

    // Add optional fields if provided
    if (providerData.is_approved !== undefined) {
        payload.is_approved = providerData.is_approved;
    }
    if (providerData.approval_status !== undefined) {
        payload.approval_status = providerData.approval_status;
    }

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to create provider profile';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data } or the provider object directly
    if (result.success && result.data) {
        return result.data;
    } else if (result.id) {
        // If result is the provider object directly
        return result;
    } else {
        throw new Error('Unexpected response format from create provider profile API');
    }
};

// Update provider profile API
export const updateProviderProfileApi = async (providerId, providerData, accessToken) => {
    // Validate provider ID
    if (!providerId) {
        throw new Error('Missing required field: provider_id is required');
    }

    // Validate access token
    if (!accessToken) {
        throw new Error('No authentication token found');
    }

    // Build payload - only include fields that are provided
    const payload = {};

    if (providerData.first_name !== undefined) {
        payload.first_name = providerData.first_name?.trim();
    }
    if (providerData.last_name !== undefined) {
        payload.last_name = providerData.last_name?.trim();
    }
    if (providerData.email !== undefined) {
        payload.email = providerData.email?.toLowerCase()?.trim();
    }
    if (providerData.bio !== undefined) {
        payload.bio = providerData.bio || null;
    }
    if (providerData.profile_url !== undefined) {
        payload.profile_url = providerData.profile_url || null;
    }
    if (providerData.sex !== undefined || providerData.gender !== undefined) {
        payload.sex = providerData.sex || providerData.gender || null;
    }
    if (providerData.image_url !== undefined) {
        payload.image_url = providerData.image_url || null;
    }
    if (providerData.status !== undefined) {
        payload.status = providerData.status;
    }
    if (providerData.is_approved !== undefined) {
        payload.is_approved = providerData.is_approved;
    }
    if (providerData.approval_status !== undefined) {
        payload.approval_status = providerData.approval_status;
    }
    if (providerData.handle !== undefined) {
        payload.handle = providerData.handle || null;
    }
    if (providerData.in_person_consultation_price !== undefined) {
        payload.in_person_consultation_price = providerData.in_person_consultation_price;
    }
    if (providerData.in_person_consultation_duration !== undefined) {
        payload.in_person_consultation_duration = providerData.in_person_consultation_duration;
    }
    if (providerData.telemedicine_consultation_price !== undefined) {
        payload.telemedicine_consultation_price = providerData.telemedicine_consultation_price;
    }
    if (providerData.telemedicine_consultation_duration !== undefined) {
        payload.telemedicine_consultation_duration = providerData.telemedicine_consultation_duration;
    }
    if (providerData.telemedicine_url !== undefined) {
        payload.telemedicine_url = providerData.telemedicine_url || null;
    }
    if (providerData.exclude_FMP_inperson !== undefined) {
        payload.exclude_FMP_inperson = !!providerData.exclude_FMP_inperson;
    }
    if (providerData.exclude_FMP_telemedicine !== undefined) {
        payload.exclude_FMP_telemedicine = !!providerData.exclude_FMP_telemedicine;
    }

    // If no fields to update, return early
    if (Object.keys(payload).length === 0) {
        throw new Error('No fields provided to update');
    }

    // Make API call to backend
    const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(payload)
        }
    );

    const result = await response.json();

    if (!response.ok || result.error) {
        const errorMessage = result.error || 'Failed to update provider profile';
        throw new Error(errorMessage);
    }

    // Backend returns { success: true, data: data } or the provider object directly
    if (result.success && result.data) {
        return result.data;
    } else if (result.id) {
        // If result is the provider object directly
        return result;
    } else {
        throw new Error('Unexpected response format from update provider profile API');
    }
};